import { useState } from 'react'
import { useUpdateEnrollmentEndorsements } from '@/api/enrollment-api'
import { useNavigate } from '@/router/navigation'
import { useEnrollmentStore } from '@/stores'
import type { EligibilityFormData } from '../../components/eligibilitySteps/types'

const initialFormData: Partial<EligibilityFormData> = {
  endorsements: [],
  trainingMode: 'in-person',
  hasLaptop: undefined
}

export function useDemographicsPage() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState<EligibilityFormData>(
    initialFormData as EligibilityFormData
  )
  const [error, setError] = useState<string | null>(null)
  const storedSessionToken = useEnrollmentStore(state => state.sessionToken)
  const updateEndorsementsMutation = useUpdateEnrollmentEndorsements()

  const sessionToken = storedSessionToken?.trim() ?? ''

  const updateFormData = (data: Partial<EligibilityFormData>) => {
    setFormData(prev => ({ ...prev, ...data }))
  }

  const saveEndorsements = async (endorsements: string[]) => {
    setError(null)

    if (!sessionToken) {
      setError('Unable to save your selections right now. Please go back and try again.')
      return false
    }

    try {
      await updateEndorsementsMutation.mutateAsync({
        sessionToken,
        endorsements
      })
      return true
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Unable to save endorsements. Please try again.'
      )
      return false
    }
  }

  const submitEndorsements = async (endorsements: string[]) => {
    const saved = await saveEndorsements(endorsements)

    if (!saved) {
      return
    }

    navigate('/thank-you', { state: { appointmentScheduled: true } })
  }

  return {
    error,
    formData,
    isSubmitting: updateEndorsementsMutation.isPending,
    navigate,
    updateFormData,
    handleContinue: () => submitEndorsements(formData.endorsements || []),
    handleContinueWithoutEndorsements: () => submitEndorsements([])
  }
}
